import {
  AddCircle,
  BorderColor,
  Description,
  DocumentScanner,
  ExpandMore,
  LocalPrintshop,
  MoreVert,
  Print,
  Save,
  Search,
  Visibility,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Menu,
  Pagination,
  TextField,
  Typography,
} from "@mui/material";
import CarService from "_api/Car/buyCar";
import useApi from "_api/index";
import Actions from "components/Car/Actions";
import TableData from "components/Car/Table";
import ChipCustomize from "components/Chip";
import CustomizeDatePicker from "components/DatePicker";
import {
  GradientButton,
  GradientButtonOutline,
} from "components/GradientButton";
import ModalCreateTransferAndReceiptBuy from "components/Modal/Car/FormDocuments/TransferAndReceiptBuy";
import ModalBuyCar from "components/Modal/Car/ModalBuyCar";
import ModalDetailCustomer from "components/Modal/Car/ModalDetailCustomer";
import CustomPagination from "components/TableExplanded/Pagination";
import { filter, set } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDateTime } from "utils/helpers/format";

const CustomersCar = () => {
  const [carsList, setCarsList] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    // Fetch data when the component mounts
    fetchData();
  }, [pagination.page, pagination.limit]);

  const fetchData = async (isClear = false) => {
    setLoading(true);
    try {
      // Simulate fetching data
      const response = await useApi.customer_car.getListCustomerCar({
        page: pagination.page || 1,
        limit: pagination.limit || 10,
        search: isClear ? "" : searchQuery,
      });
      setCarsList(response.data);
      setPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: Math.ceil(response.total / response.limit) || 1,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchQuery || searchDate) {
      if (pagination.page !== 1) {
        setPagination((prev) => ({
          ...prev,
          page: 1, // Reset to first page when searching
        }));
      } else {
        fetchData();
      }
    }
  };

  const handleClearSearch = async () => {
    setSearchQuery("");
    setSearchDate("");
    if (pagination.page !== 1) {
      setPagination((prev) => ({
        ...prev,
        page: 1, // Reset to first page when clearing search
      }));
    } else {
      setLoading(true);
      try {
        fetchData(true);
      } catch (error) {
        console.error("Error clearing search:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({
      ...prev,
      page: value + 1,
    }));
  };
  const handleRowsPerPageChange = (event) => {
    setPagination((prev) => ({
      ...prev,
      limit: event.target.value,
      page: 1, // Reset to first page when changing rows per page
    }));
  };

  const handleRowClick = (row) => {
    setSelectedRow(row);
    // Handle row click for detail view
    console.log("Row clicked for detail:", row);
    setIsDetailModalOpen(true);
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography component={"div"} sx={{ fontSize: "32px" }}>
          รายชื่อลูกค้า
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* Add any additional buttons or actions here */}
          <GradientButtonOutline
            onClick={() => {
              // Handle the click event for adding a new car
            }}
            sx={{
              px: 3,
              display: "flex",
              alignItems: "center",
              height: "48px",
              backgroundColor: "white",
            }}
          >
            <Typography style={{ marginRight: "8px" }}>ส่งออก Excel</Typography>
            <Save fontSize="medium" />
          </GradientButtonOutline>
        </Box>
      </Box>

      {/* searchQuery component can be added here if needed */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          justifyContent: "flex-end",
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              whiteSpace: "nowrap",
            }}
          >
            ค้นหาทั้งหมด:
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="ค้นหา"
            sx={{
              flex: 3,
              "& .MuiOutlinedInput-root": {
                bgcolor: "white",
                height: "40px",
              },
            }}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            alignItems: "center",
          }}
        >
          <GradientButtonOutline
            onClick={() => {
              // Handle clear action
              handleClearSearch();
            }}
            sx={{
              height: "40px",
            }}
          >
            ล้าง
          </GradientButtonOutline>
          <GradientButton
            onClick={() => {
              // Handle search action
              handleSearch();
            }}
            endIcon={<Search />}
          >
            ค้นหา
          </GradientButton>
        </Box>
      </Box>

      {/* Table or list to display the cars */}
      <TableData
        loading={loading}
        data={carsList}
        columns={[
          {
            key: "customer_idcard",
            label: "เลขบัตรประชาชน",
          },
          {
            key: "customer_name",
            label: "ชื่อ - สกุล",
          },
          {
            key: "customer_phonenumber",
            label: "เบอร์โทรศัพท์",
          },
          { key: "customer_address", label: "ที่อยู่" },
          {
            key: "action",
            label: "Actions",
            style: {
              textAlign: "center",
            },
            render: (row) => (
              <Actions
                menuList={[
                  {
                    label: "ดูรายละเอียด",
                    icon: <Visibility />,
                    onClick: () => {
                      handleRowClick(row, "detail");
                    },
                  },
                ]}
              />
            ),
          },
        ]}
      />

      <CustomPagination
        totalPage={pagination.totalPages}
        onPageChange={(event, value) => {
          handlePageChange(event, value);
        }}
        page={pagination.page - 1}
        rowsPerPage={pagination.limit}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      <ModalDetailCustomer
        open={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedRow(null); // Clear selected row when closing
        }}
        rowID={selectedRow ? selectedRow.customer_id : null}
      />
    </Box>
  );
};

export default CustomersCar;
